/**
 * Albums — covers only.
 *
 * Opening one does not open a new screen: it sets `albumId` on the shared
 * filter and hands over to the cards grid, which is why an album can then be
 * narrowed further by person or by date without leaving it.
 */

import { h, patch, replace } from "../dom";
import { api, blobUrl } from "../api";
import { store } from "../state";
import { ask } from "../modal";
import { empty, failure, fingerprint, loading, toast } from "../ui";
import { count } from "../format";
import type { AlbumSummary } from "../types";
import type { RefreshOptions, View } from "./view";

export function mount(pane: HTMLElement): View {
  let generation = 0;

  function open(a: AlbumSummary): void {
    store.setFilter({ albumId: a.id });
    store.go("cards");
  }

  async function create(): Promise<void> {
    const answer = await ask({
      title: "New album",
      body:
        "An album is a set of photographs you choose. Nothing is copied and " +
        "nothing leaves the library — a photograph can sit in several albums " +
        "at once.",
      fields: [
        {
          name: "name",
          label: "Name",
          placeholder: "Nana across her life",
          required: true,
          maxLength: 120,
        },
        {
          name: "description",
          label: "What is it for",
          hint: "Optional. Useful when somebody else opens it years from now.",
          multiline: true,
          maxLength: 500,
        },
      ],
      confirm: "Create album",
    });
    const name = answer?.name?.trim();
    if (!name) return;
    try {
      await api.createAlbum(store.libraryId, name, answer?.description?.trim() || undefined);
      toast(`Made \u201c${name}\u201d.`);
      void refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not create it", true);
    }
  }

  /**
   * Rename an album, or say something different about it.
   *
   * Both fields at once because they are one thought — "this album is not
   * quite what I said it was" — and asking twice for that is asking somebody
   * to open the same box twice.
   */
  async function edit(a: AlbumSummary): Promise<void> {
    const answer = await ask({
      title: "Rename this album",
      fields: [
        { name: "name", label: "Name", value: a.name, required: true, maxLength: 120 },
        {
          name: "description",
          label: "Description",
          value: a.description ?? "",
          multiline: true,
          maxLength: 600,
          hint: "What this album is for. Optional.",
        },
      ],
      confirm: "Save",
    });
    const name = answer?.name?.trim();
    if (!name) return;
    try {
      await api.updateAlbum(store.libraryId, a.id, {
        name,
        description: answer?.description?.trim() || null,
      });
      toast("Saved.");
      void refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not save that", true);
    }
  }

  /**
   * Delete an album, not its photographs.
   *
   * Said explicitly in the box, because "delete album" is exactly the phrase
   * that makes somebody hesitate over a family archive — and the answer is
   * reassuring, so it should be given rather than left to be worked out.
   */
  async function remove(a: AlbumSummary): Promise<void> {
    const answer = await ask({
      title: `Delete “${a.name}”?`,
      body:
        `The album goes. The ${count(a.count, "photograph")} in it stay in the ` +
        "archive — an album is a selection, not a folder.",
      fields: [],
      confirm: "Delete the album",
      destructive: true,
    });
    if (!answer) return;
    try {
      await api.deleteAlbum(store.libraryId, a.id);
      toast(`Deleted “${a.name}”.`);
      void refresh();
    } catch (e) {
      toast(e instanceof Error ? e.message : "could not delete it", true);
    }
  }

  function cardEl(a: AlbumSummary): HTMLElement {
    const cover = blobUrl(a.coverHash);
    const card = h(
      "button",
      {
        class: "album",
        type: "button",
        onclick: () => open(a),
        "data-key": a.id,
        "data-sig": fingerprint(a),
      },
      cover
        ? h("img", { src: cover, alt: "", loading: "lazy" })
        : h("div", { class: "nocover" }),
      h(
        "div",
        { class: "m" },
        h("div", { class: "nm", text: a.name }),
        h("div", {
          class: "d",
          text: `${count(a.count, "photograph")}${a.visibility === "shared" ? " · shared" : ""}`,
        }),
        a.description ? h("div", { class: "desc", text: a.description }) : null,
      ),
    );

    if (!store.can("album.manage")) return card;

    // Beside the card rather than inside it: the card is one big button that
    // opens the album, and a button inside a button is a click nobody can
    // predict the result of.
    return h(
      "div",
      { class: "albumwrap" },
      card,
      h(
        "div",
        { class: "albumacts" },
        h("button", {
          class: "btn xs ghost",
          text: "Rename",
          title: "Change the name or the description",
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            void edit(a);
          },
        }),
        h("button", {
          class: "btn xs ghost danger",
          text: "Delete",
          title: "Delete the album. The photographs stay.",
          onclick: (e: MouseEvent) => {
            e.stopPropagation();
            void remove(a);
          },
        }),
      ),
    );
  }

  async function refresh(opts: RefreshOptions = {}): Promise<void> {
    generation++;
    const mine = generation;
    // In place only over a list that is already drawn.
    const quiet = Boolean(opts.quiet && pane.querySelector(".albums"));
    if (!quiet) replace(pane, loading("albums"));

    let albums: AlbumSummary[];
    try {
      albums = await api.albums(store.libraryId);
    } catch (e) {
      if (mine !== generation || quiet) return;
      replace(pane, failure(e, () => void refresh()));
      return;
    }
    if (mine !== generation) return;

    // `album.create` is an owner capability. A contributor gets the albums and
    // no button — not a button that would come back 403.
    const newBtn = store.can("album.create")
      ? h("button", { class: "btn primary", text: "New album", onclick: create })
      : null;

    if (!albums.length) {
      replace(
        pane,
        empty({
          icon: "album",
          title: "No albums yet",
          body: [
            "An album is a hand-made selection — the ones for the funeral, the ones to print, the ones nobody can identify. Everything else in this client is generated; albums are the part somebody chooses.",
          ],
          actions: newBtn ? [newBtn] : [],
        }),
      );
      return;
    }

    const nodes = [
      h(
        "div",
        { class: "albumbar" },
        h("h2", { text: "Albums" }),
        h("div", { class: "grow" }),
        newBtn,
      ),
      h("p", {
        class: "sub",
        text: "Opening an album narrows the shared filter to it, so you can keep narrowing — one person, one summer — without leaving the album.",
      }),
      h("div", { class: "albums" }, ...albums.map(cardEl)),
    ];
    // Patched on a live update: a new cover changes one card, not the page.
    if (quiet) patch(pane, nodes);
    else replace(pane, ...nodes);
  }

  void refresh();

  return {
    refresh: (opts) => void refresh(opts),
    destroy() {
      generation++;
    },
  };
}
